import React from 'react';
import { Box, ButtonBase, Typography, IconButton, Button } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

const CARD_BG = '#141416';
const TEXT_SECONDARY = 'rgba(255,255,255,0.7)';
const TEXT_TERTIARY = 'rgba(255,255,255,0.4)';

export function YouCard({ children, sx = {}, ...props }) {
  return (
    <Box
      sx={{
        backgroundColor: CARD_BG,
        borderRadius: '12px',
        padding: '16px',
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
}

export function YouRow({
  icon,
  title,
  subtitle,
  onClick,
  trailing,
  accent = false,
  dense = false,
  selected = false,
  sx = {},
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: dense ? '12px' : '14px',
        width: '100%',
        minHeight: dense ? 44 : 56,
        padding: dense ? '0 12px' : '14px',
        borderRadius: '10px',
        textAlign: 'left',
        justifyContent: 'flex-start',
        ...(selected ? { backgroundColor: 'rgba(255,255,255,0.06)' } : null),
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
        '&:active': { backgroundColor: 'rgba(255,255,255,0.06)' },
        ...sx,
      }}
    >
      {icon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: accent ? 'var(--accent)' : TEXT_SECONDARY,
            '& .MuiSvgIcon-root': { fontSize: 24 },
          }}
        >
          {icon}
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: dense ? 15 : 16,
            fontWeight: 500,
            color: '#fff',
          }}
        >
          {title}
        </Typography>
        {subtitle && !dense && (
          <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,0.55)' }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* En dense la rangee tient sur une ligne : le sous-titre part en queue. */}
      {subtitle && dense && (
        <Typography sx={{ fontSize: 12, color: TEXT_TERTIARY, flexShrink: 0 }}>
          {subtitle}
        </Typography>
      )}

      {trailing}

      {!dense && <ChevronRightIcon sx={{ fontSize: 20, color: TEXT_TERTIARY }} />}
    </ButtonBase>
  );
}

export function YouRowGroup({ children, sx = {} }) {
  return (
    <YouCard sx={{ padding: '4px', ...sx }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>{children}</Box>
    </YouCard>
  );
}

export function SectionLabel({ children, action, sx = {} }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        ...sx,
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: '1.2px',
          color: 'rgba(255,255,255,0.55)',
          textTransform: 'uppercase',
        }}
      >
        {children}
      </Typography>
      {action}
    </Box>
  );
}

export function SubScreenHeader({ title, onBack, sx = {} }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', ...sx }}>
      {/* En maitre-detail la section n'est pas un sous-ecran : sans onBack, pas
          de chevron et donc pas de marge negative a compenser. */}
      {onBack && (
        <IconButton aria-label="Back" onClick={onBack} sx={{ marginLeft: '-8px', color: '#fff' }}>
          <ChevronLeftIcon sx={{ fontSize: 28 }} />
        </IconButton>
      )}
      <Typography sx={{ fontSize: 24, fontWeight: 300, color: '#fff' }}>{title}</Typography>
    </Box>
  );
}

export function PrimaryButton({ children, onClick, sx = {}, ...props }) {
  return (
    <Button
      variant="contained"
      disableElevation
      fullWidth
      onClick={onClick}
      sx={{
        height: 46,
        backgroundColor: 'var(--button)',
        color: 'var(--button-text)',
        fontSize: 16,
        fontWeight: 600,
        borderRadius: '8px',
        textTransform: 'none',
        '&:hover': { backgroundColor: 'var(--button-hover)' },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Button>
  );
}

export const softButtonSx = {
  borderRadius: '8px',
  textTransform: 'none',
  color: '#fff',
  backgroundColor: 'rgba(255,255,255,0.08)',
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
};

export const dangerButtonSx = {
  ...softButtonSx,
  color: '#ff6b62',
  backgroundColor: 'rgba(244,67,54,0.14)',
  '&:hover': { backgroundColor: 'rgba(244,67,54,0.22)' },
};

export const accentButtonSx = {
  borderRadius: '8px',
  textTransform: 'none',
  color: 'var(--button-text)',
  backgroundColor: 'var(--button)',
  '&:hover': { backgroundColor: 'var(--button-hover)' },
};
